import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  addDoc,
  Timestamp,
  where,
  deleteField,
} from 'firebase/firestore'
import { db } from '@/config/firebase'
import { ChatMessage } from '@/types/chat'

interface ChatMessageDoc {
  senderId: string
  content: string
  isRead: boolean
  createdAt?: Timestamp
}

export const createDirectChatId = (userIdA: string, userIdB: string): string => {
  return [userIdA, userIdB].sort().join('_')
}

const mapDocToChatMessage = (id: string, chatId: string, raw: ChatMessageDoc): ChatMessage => {
  return {
    id,
    chatId,
    senderId: raw.senderId,
    content: raw.content,
    isRead: raw.isRead,
    createdAt: raw.createdAt?.toDate() ?? new Date(),
  }
}

export const subscribeDirectChatMessages = (
  userIdA: string,
  userIdB: string,
  onMessages: (messages: ChatMessage[]) => void
) => {
  const chatId = createDirectChatId(userIdA, userIdB)
  return subscribeChatMessages(chatId, onMessages)
}

export const subscribeChatMessages = (
  chatId: string,
  onMessages: (messages: ChatMessage[]) => void
) => {
  const normalizedChatId = String(chatId)
  const messagesRef = collection(db, 'chats', normalizedChatId, 'messages')
  const messagesQuery = query(messagesRef, orderBy('createdAt', 'asc'))

  return onSnapshot(messagesQuery, (snapshot) => {
    const messages = snapshot.docs.map((item) => {
      const raw = item.data() as ChatMessageDoc
      return mapDocToChatMessage(item.id, normalizedChatId, raw)
    })
    onMessages(messages)
  })
}

export const sendDirectChatMessage = async (
  userIdA: string,
  userIdB: string,
  senderId: string,
  content: string
) => {
  const chatId = createDirectChatId(userIdA, userIdB)
  await ensureChatDocument(chatId, [userIdA, userIdB])
  await sendChatMessageRealtime(chatId, senderId, content)
}

export const subscribeNewChatMessages = (
  chatId: string,
  afterDate: Date,
  onMessages: (messages: ChatMessage[]) => void
) => {
  const normalizedChatId = String(chatId)
  const messagesRef = collection(db, 'chats', normalizedChatId, 'messages')
  const afterTimestamp = Timestamp.fromDate(afterDate)
  const messagesQuery = query(
    messagesRef,
    orderBy('createdAt', 'asc'),
    where('createdAt', '>', afterTimestamp)
  )

  return onSnapshot(messagesQuery, (snapshot) => {
    const messages = snapshot.docs.map((item) => {
      const raw = item.data() as ChatMessageDoc
      return mapDocToChatMessage(item.id, normalizedChatId, raw)
    })
    onMessages(messages)
  })
}


/**
 * 相手へ未読通知を書き込む。
 *
 * ドキュメントIDには Firebase UID を使う。Firestore のセキュリティルールは
 * request.auth.uid としか比較できないため、DBの数値IDでは本人以外の読み取りを
 * 禁止できないため。fromUserId には難読化済みのDBユーザーIDをそのまま格納し、
 * 参加者一覧の profile.id と直接突き合わせられるようにする。
 */
export const notifyPartnerOfNewMessage = async (
  chatId: string,
  partnerFirebaseUid: string,
  senderDbUserId: string,
  content: string
): Promise<void> => {
  if (!partnerFirebaseUid || !senderDbUserId) return

  const notifRef = doc(db, 'user_notifications', partnerFirebaseUid)
  await setDoc(
    notifRef,
    {
      unreadChats: {
        [chatId]: {
          content: content.slice(0, 100),
          fromUserId: senderDbUserId,
          at: serverTimestamp(),
        },
      },
    },
    { merge: true }
  )
}

export const clearChatNotification = async (
  chatId: string,
  currentFirebaseUid: string
): Promise<void> => {
  if (!currentFirebaseUid) return
  const notifRef = doc(db, 'user_notifications', currentFirebaseUid)
  await setDoc(
    notifRef,
    { [`unreadChats.${chatId}`]: deleteField() },
    { merge: true }
  )
}

export interface UnreadNotification {
  fromUserId: string
  content: string
  at: Date | null
}

export const subscribeUserNotifications = (
  currentFirebaseUid: string,
  onUpdate: (unreadByUserId: Record<string, number>, unreadDetails: Record<string, UnreadNotification>) => void
): (() => void) => {
  if (!currentFirebaseUid) return () => {}

  const notifRef = doc(db, 'user_notifications', currentFirebaseUid)
  return onSnapshot(notifRef, (snapshot) => {
    const data = snapshot.data() as Record<string, any> | undefined
    const unreadByUser: Record<string, number> = {}
    const unreadDetails: Record<string, UnreadNotification> = {}
    // setDoc({[`unreadChats.${chatId}`]: ...}, {merge: true}) はドット区切りのフィールドパスとして
    // 解釈されるため、Firestore上では unreadChats フィールド配下のネストしたマップとして保存される
    // (フラットキー "unreadChats.xxx" としては保存されない)
    const unreadChats = (data ?? {})['unreadChats'] as Record<string, any> | undefined
    for (const value of Object.values(unreadChats ?? {})) {
      if (!value) continue
      const entry = value as { content: string; fromUserId: string; at: any }
      const fromUserId = String(entry.fromUserId)
      unreadByUser[fromUserId] = (unreadByUser[fromUserId] ?? 0) + 1
      // 同じ送信者の最新メッセージを保持
      const msgDate = entry.at?.seconds ? new Date(entry.at.seconds * 1000) : null
      if (!unreadDetails[fromUserId] || (msgDate && (!unreadDetails[fromUserId].at || msgDate > unreadDetails[fromUserId].at!))) {
        unreadDetails[fromUserId] = {
          fromUserId,
          content: entry.content,
          at: msgDate,
        }
      }
    }
    onUpdate(unreadByUser, unreadDetails)
  })
}

/**
 * チャットドキュメントに参加者の Firebase UID を登録する。
 *
 * Firestore のセキュリティルールがこの participantUids を見て読み書きを
 * 判定するため、メッセージ送受信より先に必ず呼ぶ必要がある。
 */
export const ensureChatDocument = async (
  chatId: string,
  participantUids: string[]
): Promise<void> => {
  if (!chatId || !participantUids?.length) return
  const chatRef = doc(db, 'chats', String(chatId))
  await setDoc(
    chatRef,
    {
      participantUids: [...participantUids].sort(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  )
}

export const sendChatMessageRealtime = async (
  chatId: string,
  senderId: string,
  content: string
) => {
  const normalizedChatId = String(chatId)

  await addDoc(collection(db, 'chats', normalizedChatId, 'messages'), {
    senderId,
    content,
    isRead: false,
    createdAt: serverTimestamp(),
  })
}
